import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCw, Send, CheckCircle2, ChevronDown, ChevronUp, PenLine, User, CalendarClock,
  AlertTriangle, Archive, Undo2, Trash2, Square, CheckSquare,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { insertBookingToken } from '@/lib/bookingToken';

const API = '/api';

// Personal Letters — staged review-and-send queue (warm-list Part 1).
// Every letter is editable until sent; the ONLY send is the explicit button below.

function LetterCard({ letter, actor, onChanged, selectable, checked, onToggle }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(letter.subject || '');
  const [body, setBody] = useState(letter.body || '');
  const [email, setEmail] = useState(letter.recipient_email || '');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const bodyRef = useRef(null);
  // Only a STAGED letter is sendable or editable. This used to be `status !== 'sent'`,
  // which put voided letters in the staged group with a live Send button.
  const isStaged = letter.status === 'staged';
  const isVoided = letter.status === 'voided';
  const sent = !isStaged;                     // "locked" — no edit, no send
  // Deliverability, from the last MailerLite resolution. Anything not 'active' can never
  // receive, so the send is blocked here as well as refused by the backend pre-flight.
  const undeliverable = letter.deliverability_status
    && letter.deliverability_status !== 'active';
  const dirty = subject !== (letter.subject || '') || body !== (letter.body || '')
    || email !== (letter.recipient_email || '');

  const save = async () => {
    setSaving(true); setError('');
    try {
      await axios.put(`${API}/personal-letters/${letter.id}`, {
        subject, body, recipient_email: email,
      });
      onChanged?.();
    } catch (e) { setError(e.response?.data?.detail || 'Save failed.'); }
    setSaving(false);
  };

  const handled = async () => {
    const reason = window.prompt(
      'Handled elsewhere — optional one-line note (sent from Outlook, said in person, '
      + 'overtaken by events…). Leave blank if you like.\n\n'
      + 'The letter and its content are KEPT. It just leaves the send queue.', '');
    if (reason === null) return;            // cancelled
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/personal-letters/${letter.id}/void`, { actor, reason });
      onChanged?.();
    } catch (e) { setError(e.response?.data?.detail || 'Could not update.'); }
    setBusy(false);
  };

  const unvoid = async () => {
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/personal-letters/${letter.id}/unvoid`, {});
      onChanged?.();
    } catch (e) { setError(e.response?.data?.detail || 'Could not restore.'); }
    setBusy(false);
  };

  const del = async () => {
    if (!window.confirm(
      `DELETE this letter to ${letter.recipient_name}?\n\n`
      + 'This destroys the record permanently. Use it only for a genuine mistake or a '
      + 'duplicate.\n\nIf it was simply handled another way, press Cancel and use '
      + '"Handled elsewhere" instead — that keeps the record.')) return;
    setBusy(true); setError('');
    try {
      await axios.delete(`${API}/personal-letters/${letter.id}`);
      onChanged?.();
    } catch (e) { setError(e.response?.data?.detail || 'Delete failed.'); }
    setBusy(false);
  };

  const send = async () => {
    if (!window.confirm(`Send this letter to ${email} from ${letter.sender === 'randall' ? 'Randall Henry <randall@henry-griffitts.com>' : 'HG Support'}?\n\nThis is the real send.`)) return;
    setSending(true); setError('');
    try {
      if (dirty) {
        await axios.put(`${API}/personal-letters/${letter.id}`, {
          subject, body, recipient_email: email,
        });
      }
      await axios.post(`${API}/personal-letters/${letter.id}/send`, { actor });
      setOpen(false); onChanged?.();
    } catch (e) { setError(e.response?.data?.detail || 'Send failed.'); }
    setSending(false);
  };

  return (
    <div className={`bg-hg-surface border rounded-sm ${sent ? 'border-hg-border/50 opacity-70' : 'border-hg-border'}`}
         data-testid={`letter-${letter.id}`}>
      <div className="flex items-center">
      {selectable && (
        <button onClick={(e) => { e.stopPropagation(); onToggle?.(letter.id); }}
          data-testid={`letter-check-${letter.id}`} aria-label="select letter"
          className="pl-4 pr-1 py-3 text-hg-text-dim hover:text-hg-green flex-shrink-0">
          {checked ? <CheckSquare size={14} className="text-hg-green" /> : <Square size={14} />}
        </button>
      )}
      <button onClick={() => setOpen(!open)} className="w-full text-left px-4 py-3 flex items-center gap-3">
        {sent ? <CheckCircle2 size={14} className="text-hg-green flex-shrink-0" />
              : <PenLine size={14} className="text-hg-green flex-shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-sans text-hg-text truncate">
            {letter.recipient_name}
            <span className="text-hg-text-dim font-normal"> · {letter.recipient_email || 'no email'}</span>
          </div>
          <div className="text-[11px] font-sans text-hg-text-dim truncate mt-0.5">{letter.subject}</div>
        </div>
        <span className={`text-[10px] font-sans uppercase tracking-wider rounded-sm px-1.5 py-0.5 border flex-shrink-0 ${
          letter.sender === 'randall' ? 'border-hg-green/40 text-hg-green' : 'border-blue-400/40 text-blue-300'
        }`}>
          <User size={9} className="inline mr-1" />{letter.sender === 'randall' ? 'From Randall' : 'From HG'}
        </span>
        {undeliverable && (
          <span className="text-[10px] font-sans uppercase tracking-wider text-red-300 border border-red-400/50 bg-red-400/10 rounded-sm px-1.5 py-0.5 flex-shrink-0"
            data-testid={`letter-undeliverable-${letter.id}`}
            title={`${letter.recipient_email} is '${letter.deliverability_status}' in MailerLite`
              + (letter.deliverability_since ? ` since ${String(letter.deliverability_since).slice(0, 10)}` : '')
              + ' — this address cannot receive Hub email and the send will be refused.'}>
            <AlertTriangle size={9} className="inline mr-1" />can't receive
          </span>
        )}
        {letter.status === 'sent'
          ? <span className="text-[10px] font-sans text-hg-green flex-shrink-0">sent {String(letter.sent_at).slice(0, 10)}</span>
          : isVoided
          ? <span className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim border border-hg-border rounded-sm px-1.5 py-0.5 flex-shrink-0 line-through">voided</span>
          : <span className="text-[10px] font-sans uppercase tracking-wider text-amber-300 border border-amber-400/40 rounded-sm px-1.5 py-0.5 flex-shrink-0">staged</span>}
        {open ? <ChevronUp size={14} className="text-hg-text-dim flex-shrink-0" /> : <ChevronDown size={14} className="text-hg-text-dim flex-shrink-0" />}
      </button>
      </div>

      {open && (
        <div className="px-4 pb-4 space-y-2 border-t border-hg-border/50 pt-3">
          {letter.note && <div className="text-[11px] font-sans text-amber-300">{letter.note}</div>}
          {isVoided && (
            <div className="text-[11px] font-sans text-hg-text-dim border border-hg-border rounded-sm px-2.5 py-2">
              Handled elsewhere{letter.voided_at ? ` on ${String(letter.voided_at).slice(0, 10)}` : ''}
              {letter.voided_by ? ` by ${letter.voided_by}` : ''}. It is out of the queue and
              cannot be sent. {letter.voided_reason}
            </div>
          )}
          {undeliverable && (
            <div className="text-[11px] font-sans text-red-300 border border-red-400/40 bg-red-400/5 rounded-sm px-2.5 py-2"
              data-testid={`letter-undeliverable-note-${letter.id}`}>
              {letter.recipient_email} is <strong>{letter.deliverability_status}</strong> in MailerLite
              {letter.deliverability_since ? ` (since ${String(letter.deliverability_since).slice(0, 10)})` : ''} —
              it cannot receive Hub email, so this letter will not send. Reach them another way,
              or get a re-opt-in first. Send is disabled here and refused by the server.
            </div>
          )}
          <input value={email} onChange={(e) => setEmail(e.target.value)} disabled={sent}
            className="w-full bg-hg-bg border border-hg-border rounded-sm px-2.5 py-1.5 text-xs font-sans text-hg-text disabled:opacity-60"
            placeholder="Recipient email" />
          <input value={subject} onChange={(e) => setSubject(e.target.value)} disabled={sent}
            className="w-full bg-hg-bg border border-hg-border rounded-sm px-2.5 py-1.5 text-xs font-sans text-hg-text disabled:opacity-60"
            placeholder="Subject" />
          <textarea ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)} rows={10} disabled={sent}
            className="w-full bg-hg-bg border border-hg-border rounded-sm px-2.5 py-2 text-[13px] font-sans text-hg-text leading-relaxed resize-y disabled:opacity-60" />
          {!sent && (
            <button onClick={() => setBody(insertBookingToken(bodyRef.current, body))}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-green hover:border-hg-green"
              title="Becomes a clean labeled text link in the sent letter"
              data-testid={`booking-btn-${letter.id}`}>
              <CalendarClock size={11} /> Insert booking link
            </button>
          )}
          {error && <div className="text-xs text-red-400 font-sans">{error}</div>}
          {/* Disposal actions. "Handled elsewhere" is the DEFAULT and the prominent one:
              nine times out of ten a staged letter was dealt with in Outlook or in person,
              and the record should stay. Delete is for mistakes only and never touches a
              sent letter — the server refuses that too. */}
          {letter.status !== 'sent' && (
            <div className="flex gap-2 flex-wrap items-center pt-1 border-t border-hg-border/50">
              {!isVoided ? (
                <button onClick={handled} disabled={busy} data-testid={`letter-handled-${letter.id}`}
                  title="Takes it out of the queue and keeps the record"
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-sans border border-amber-400/60 text-amber-300 rounded-sm hover:bg-amber-400/10 disabled:opacity-40">
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <Archive size={12} />}
                  Handled elsewhere
                </button>
              ) : (
                <button onClick={unvoid} disabled={busy} data-testid={`letter-unvoid-${letter.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-green">
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />}
                  Put back in the queue
                </button>
              )}
              <button onClick={del} disabled={busy} data-testid={`letter-delete-${letter.id}`}
                title="For genuine mistakes and duplicates only — this destroys the record"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-sans text-hg-text-dim rounded-sm hover:text-red-400">
                <Trash2 size={12} /> Delete
              </button>
              <span className="text-[10px] font-sans text-hg-text-dim">
                Delete is for mistakes only. Sent letters can never be deleted.
              </span>
            </div>
          )}
          {!sent && (
            <div className="flex gap-2">
              <button onClick={save} disabled={saving || !dirty}
                className="px-3 py-2 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-text disabled:opacity-40">
                {saving ? <Loader2 size={12} className="animate-spin" /> : 'Save edits'}
              </button>
              <button onClick={send} disabled={sending || undeliverable} data-testid={`letter-send-${letter.id}`}
                title={undeliverable ? `${letter.recipient_email} cannot receive Hub email` : undefined}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40">
                {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                Send to {letter.recipient_name?.split(' ')[0]}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PersonalLetters() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sel, setSel] = useState([]);            // selected letter ids
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/personal-letters?include_archived=true`);
      setData(r.data);
    } catch (e) { setError(e.response?.data?.detail || 'Could not load letters.'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const letters = data?.letters || [];
  // Group on the ACTUAL status. This was `status !== 'sent'`, which swept voided letters
  // into Staged with a live Send button while the dashboard counted only status='staged' —
  // so the two disagreed (7 vs 5) and the page was the wrong one.
  const live = letters.filter((l) => !l.archived_at);
  const archived = letters.filter((l) => l.archived_at);
  const staged = live.filter((l) => l.status === 'staged');
  const voidedOnes = live.filter((l) => l.status === 'voided');
  const sentOnes = live.filter((l) => l.status === 'sent');
  const other = live.filter((l) => !['staged', 'voided', 'sent'].includes(l.status));
  const toggle = (id) => setSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const clearSel = () => setSel([]);

  // Bulk "handled elsewhere" — four letters, one action. The server refuses a selection
  // containing a SENT letter outright rather than silently skipping it.
  const bulkHandled = async () => {
    const reason = window.prompt(
      `Mark ${sel.length} letter(s) as handled elsewhere?

`
      + 'Optional one-line note. The records are KEPT — they just leave the send queue.', '');
    if (reason === null) return;
    setBulkBusy(true); setNotice('');
    try {
      const r = await axios.post(`${API}/personal-letters/bulk-void`,
        { ids: sel, actor: user?.email || 'admin', reason });
      setNotice(`${r.data.voided} letter(s) moved to Handled elsewhere. `
        + `Awaiting your Send is now ${r.data.awaiting_send_now}.`);
      clearSel(); await load();
    } catch (e) { setNotice(e.response?.data?.detail || 'Bulk action failed.'); }
    setBulkBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="personal-letters">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[11px] font-sans text-hg-text-dim">
          Randall-approved personal letters, staged for review. Edit freely — nothing sends
          until you press a letter's Send button. Suppression is checked at send.
        </p>
        <button onClick={load} disabled={loading}
          className="inline-flex items-center gap-1 text-[11px] font-sans text-hg-text-dim hover:text-hg-green disabled:opacity-40">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh
        </button>
      </div>
      {error && <div className="text-xs text-red-400 font-sans">{error}</div>}
      {loading && !data && <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-hg-green" /></div>}
      {data && (
        <>
          {notice && (
            <div className="text-xs font-sans text-hg-text bg-hg-green/15 border border-hg-green/40 rounded-sm px-3 py-2"
              data-testid="pl-notice">{notice}</div>
          )}
          {sel.length > 0 && (
            <div className="flex items-center gap-3 flex-wrap text-xs font-sans bg-amber-400/10 border border-amber-400/40 rounded-sm px-3 py-2"
              data-testid="pl-bulk-bar">
              <span className="text-amber-200">{sel.length} selected</span>
              <button onClick={bulkHandled} disabled={bulkBusy} data-testid="pl-bulk-handled"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-amber-400/60 text-amber-200 rounded-sm hover:bg-amber-400/10 disabled:opacity-40">
                {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Archive size={12} />}
                Handled elsewhere
              </button>
              <button onClick={clearSel} className="text-hg-text-dim hover:text-hg-text">clear</button>
              <span className="text-hg-text-dim">Sent letters cannot be included — the server refuses the whole action rather than skipping them.</span>
            </div>
          )}
          <div className="space-y-2">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">
                Awaiting your Send ({staged.length})
              </div>
              {staged.length > 0 && (
                <button onClick={() => setSel(staged.map((l) => l.id))} data-testid="pl-select-all"
                  className="text-[10px] font-sans text-hg-text-dim hover:text-hg-green underline">
                  select all {staged.length}
                </button>
              )}
            </div>
            {staged.map((l) => <LetterCard key={l.id} letter={l} actor={user?.email || 'admin'} onChanged={load}
              selectable checked={sel.includes(l.id)} onToggle={toggle} />)}
            {staged.length === 0 && <div className="text-[12px] font-sans text-hg-text-dim italic">Nothing awaiting send.</div>}
          </div>
          {voidedOnes.length > 0 && (
            <div className="space-y-2 pt-2">
              <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">
                Handled elsewhere ({voidedOnes.length}) — out of the queue, not sendable
              </div>
              {voidedOnes.map((l) => <LetterCard key={l.id} letter={l} actor={user?.email || 'admin'} onChanged={load} />)}
            </div>
          )}
          {archived.length > 0 && (
            <div className="space-y-2 pt-2">
              <button onClick={() => setShowArchived(!showArchived)} data-testid="pl-archived-toggle"
                className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim hover:text-hg-text inline-flex items-center gap-1">
                {showArchived ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                Archived ({archived.length}) — out of view, not counted as awaiting send
              </button>
              {showArchived && archived.map((l) => (
                <LetterCard key={l.id} letter={l} actor={user?.email || 'admin'} onChanged={load} />
              ))}
            </div>
          )}
          {other.length > 0 && (
            <div className="space-y-2 pt-2">
              <div className="text-[10px] font-sans uppercase tracking-wider text-amber-300">
                Unrecognised status ({other.length}) — {[...new Set(other.map((l) => l.status))].join(', ')}
              </div>
              {other.map((l) => <LetterCard key={l.id} letter={l} actor={user?.email || 'admin'} onChanged={load} />)}
            </div>
          )}
          {sentOnes.length > 0 && (
            <div className="space-y-2 pt-2">
              <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">Sent ({sentOnes.length})</div>
              {sentOnes.map((l) => <LetterCard key={l.id} letter={l} actor={user?.email || 'admin'} onChanged={load} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
